import React, { useState, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client/react/hooks';
import { AlertContext } from 'lib/alert-context/alert-context';
import { Form } from 'formik';
import { Field, FieldProps } from 'formik';
import { FormikProps, FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { RouteComponentProps } from 'react-router';
import classnames from 'classnames';
import { MutationFunctionOptions } from '@apollo/client';
import Styled from 'styled';

import Image from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ScrollToTop from 'lib/scroll/scroll-to-top';

import iconClose from 'four-nets/icons/svg/icon_close.svg';
import deleteIcon from 'four-nets/icons/svg/icon_delete.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import { PostVisibility } from 'four-nets/photoblog/create-post/mutations/create-post.graphql.types';
import SelectionItems from 'four-nets/photoblog/my-selection/selection-items/selection-items';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import query, { AlbumQuery, AlbumQueryVariables } from 'four-nets/photoblog/posts/album/album-query.graphql';
import DateSelect from 'four-nets/user/settings/date-select/date-select';
import GeoSelect from 'four-nets/geo/geo-select/geo-select';
import Input from 'four-nets/ui-v2/form/input/input';

import { AlbumWeddingFragment } from '../../album-wedding.graphql';

import AlbumBadges from './album-badges/album-badges';
import updateAlbumMutation, {
    UpdateAlbumMutation,
    UpdateAlbumMutationVariables,
} from './mutations/update-album.graphql';
import removePostMutation, { RemovePostMutation, RemovePostMutationVariables } from './mutations/remove.graphql';
import defaultStyles from './info-edit.scss';

export interface FormValues {
    content: string;
    title: string;
    visibility: PostVisibility;

    brideName: string;
    groomName: string;
    weddingCounty: string | null;
    weddingCountry: string | null;
    weddingCity: string;
    day: string;
    month: string;
    year: string;
}

interface Params {
    slug: string;
    username: string;
}

export interface Props extends RouteComponentProps<Params> {
    styles: typeof defaultStyles;
    refetch: (variables?: any) => any | Promise<any>;
    refetchQueries?: any[];
}

const InfoEdit: React.FC<Props> = props => {
    const { retry, confirm } = useContext(AlertContext);
    const { viewer } = useContext(ViewerContext);

    const {
        styles,
        match: {
            params: { username, slug },
        },
        history,
    } = props;

    const { data } = useQuery<AlbumQuery, AlbumQueryVariables>(query, {
        variables: { slug, username },
        fetchPolicy: 'cache-first',
    });

    const [selectionItemIds, setSelectionItemIds] = useState<Set<string>>(new Set());

    const [removePostFn] = useMutation<RemovePostMutation, RemovePostMutationVariables>(removePostMutation);
    const [updateAlbumFn, { loading }] = useMutation<UpdateAlbumMutation, UpdateAlbumMutationVariables>(
        updateAlbumMutation
    );

    useEffect(() => {
        if (data && data.postBySlug && data.postBySlug.selectionItems) {
            const defaultSelectionItemsIds = new Set();
            data.postBySlug.selectionItems.edges.map(edge => {
                defaultSelectionItemsIds.add(edge!.node!.id!);
            });
            setSelectionItemIds(defaultSelectionItemsIds as any);
        }
    }, [data]);

    if (!Boolean(data && data.postBySlug)) {
        return null;
    }

    const album = data!.postBySlug!;
    const { title, visibility, content, images, isWeddingAlbum } = album;

    const validate = (values: FormValues) => {
        const errors: { [k in keyof FormValues]?: Array<React.ReactElement<FormattedMessage>> | undefined } = {};
        const postErrors = mergeValidators([emptyValidator])(values.title);
        if (postErrors) {
            errors.title = postErrors;
        }
        return errors;
    };

    const performDelete = (
        remove: (
            options?: MutationFunctionOptions<RemovePostMutation, RemovePostMutationVariables> | undefined
        ) => Promise<any>
    ) => {
        return remove({ variables: { input: { id: album!.id } } });
    };

    const onAccept = () => {
        performDelete(removePostFn)
            .then(() => history.push('/blogs/'))
            .catch((err: Error) =>
                retry!({
                    body: err.message,
                    onAccept,
                })
            );
    };

    const handleSubmit = (values: FormValues) => {
        const { day, month, year, ...rest } = values;

        const weddingDate = new Date();
        weddingDate.setDate(Number(day));
        weddingDate.setMonth(Number(month));
        weddingDate.setFullYear(Number(year));

        return updateAlbumFn({
            refetchQueries: props.refetchQueries,
            variables: {
                input: {
                    ...rest,
                    id: album!.id!,
                    selectionItemIds: Array.from(selectionItemIds),
                    weddingDate: weddingDate.toISOString(),
                    weddingInfo: rest.content,
                },
            },
        });
    };

    const RemoveButton = (
        <button
            type='button'
            className={styles.deleteButton}
            onClick={() =>
                confirm!({
                    body: (
                        <React.Fragment>
                            <FormattedMessage id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_ALBUM' values={{ gender: 'v' }} />?
                        </React.Fragment>
                    ),
                    onAccept,
                    acceptButtonType: 'warn',
                    acceptButtonLabel: <FormattedMessage id='DELETE_ALBUM' values={{ gender: 'v' }} />,
                })
            }
        >
            <SVGIcon style={{ image: styles.image }} src={deleteIcon} />
            <FormattedMessage id='DELETE_ALBUM' values={{ gender: 'v' }} />
        </button>
    );

    const TitleImage = (
        <div className={styles.titleImage}>
            <Image
                sizes={{ xs: 250, md: 350 }}
                aspectFill={true}
                src={
                    (images &&
                        images.edges &&
                        images.edges.length &&
                        images.edges[0] &&
                        images.edges[0]!.node &&
                        images.edges[0]!.node!.photoFile &&
                        images.edges[0]!.node!.photoFile.src) ||
                    ''
                }
                srcSet={
                    (images &&
                        images.edges &&
                        images.edges.length &&
                        images.edges[0] &&
                        images.edges[0]!.node &&
                        images.edges[0]!.node!.photoFile &&
                        images.edges[0]!.node!.photoFile.srcset) ||
                    ''
                }
            />
        </div>
    );

    const onClick = (dirty: boolean) => {
        const navigate = () => history.push(album.href);
        if (dirty) {
            confirm!({
                body: (
                    <>
                        <FormattedMessage id='LEAVE_WITHOUT_SAVING' />?
                    </>
                ),
                onAccept: navigate,
                acceptButtonLabel: <FormattedMessage id='LEAVE' />,
            });
        } else {
            navigate();
        }
    };

    if (!viewer || viewer.username !== album.user!.username) {
        return <Unauthorized />;
    }

    const wedding = (album &&
        album.user &&
        album.user.photoBlog &&
        album.user.photoBlog.details)! as AlbumWeddingFragment;

    const date = new Date(Date.parse(wedding!.weddingDate || ''));
    const day = String(date.getDate());
    const month = String(date.getMonth());
    const year = String(date.getFullYear());

    const [brideName, groomName] = (title || ' a ').split(' a ');

    return (
        <Formik
            initialValues={{
                title: title || '',
                visibility: visibility || PostVisibility.POST_VISIBILITY_AUTHENTICATED,
                content: content || '',
                brideName,
                groomName,
                weddingCounty: (wedding && wedding.county && wedding.county.id) || '',
                weddingCountry: (wedding && wedding.country && wedding.country.id) || '',
                weddingCity: wedding.weddingCity || '',
                day,
                month,
                year,
            }}
            validate={validate}
            onSubmit={(values: FormValues, actions: FormikHelpers<FormValues>) => {
                actions.setSubmitting(true);
                const onAccept = () =>
                    handleSubmit(values)
                        .then(() => {
                            props.history.push({
                                pathname: `/blog/${username}/album/${slug}/`,
                                state: { ommitFirst: true },
                            });
                        })
                        .catch((err: Error) => {
                            retry!({ onAccept, body: err.message });
                        });
                onAccept();
            }}
            render={(formikBag: FormikProps<FormValues>) => (
                <Form>
                    <ScrollToTop />

                    <LoadingWrapperView loading={formikBag.isSubmitting || loading}>
                        <div className={styles.wrapper}>
                            <div className={styles.form}>
                                <div className={classnames(styles.fields, { [styles.isWeddingAlbum]: isWeddingAlbum })}>
                                    {isWeddingAlbum ? (
                                        <>
                                            <Field name='brideName'>
                                                {({ field }: FieldProps) => (
                                                    <label>
                                                        <FormattedMessage id='BRIDE'>
                                                            {msg => <span className={styles.label}>{msg}</span>}
                                                        </FormattedMessage>
                                                        <Input
                                                            errors={
                                                                formikBag.errors.brideName
                                                                    ? (formikBag.errors.brideName as any)
                                                                    : undefined
                                                            }
                                                            touched={formikBag.touched.brideName}
                                                            {...field}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                            <Field name='groomName'>
                                                {({ field }: FieldProps) => (
                                                    <label className={styles.weddingAlbumField}>
                                                        <FormattedMessage id='GROOM'>
                                                            {msg => <span className={styles.label}>{msg}</span>}
                                                        </FormattedMessage>
                                                        <Input
                                                            errors={
                                                                formikBag.errors.groomName
                                                                    ? (formikBag.errors.groomName as any)
                                                                    : undefined
                                                            }
                                                            touched={formikBag.touched.groomName}
                                                            {...field}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                            <Field name='content'>
                                                {({ field }: FieldProps<FormValues>) => (
                                                    <label className={styles.weddingAlbumField}>
                                                        <div className={styles.label}>
                                                            <FormattedMessage id='ALBUM_DESCRIPTION' />
                                                        </div>
                                                        <textarea
                                                            className={styles.content}
                                                            name={field.name}
                                                            value={field.value || ''}
                                                            onBlur={field.onBlur}
                                                            onChange={field.onChange}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                            <label className={styles.weddingAlbumField}>
                                                <FormattedMessage id='WEDDING_DATE'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <DateSelect
                                                    style={{ label: styles.dateLabel }}
                                                    dayField={formikBag.getFieldProps('day')}
                                                    monthField={formikBag.getFieldProps('month')}
                                                    yearField={formikBag.getFieldProps('year')}
                                                />
                                            </label>
                                            <label className={styles.weddingAlbumField}>
                                                <FormattedMessage id='WEDDING_LOCATION'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <GeoSelect
                                                    style={{ wrapper: styles.geoSelectWrapper }}
                                                    {...formikBag.getFieldProps('weddingCounty')}
                                                    countyId={formikBag.values.weddingCounty}
                                                    countryId={formikBag.values.weddingCountry}
                                                    allowCountrySelection={true}
                                                    onChange={(selectedCountyID, selectedCountryID) => {
                                                        formikBag.setFieldValue('weddingCounty', selectedCountyID);
                                                        formikBag.setFieldValue('weddingCountry', selectedCountryID);
                                                    }}
                                                />
                                            </label>
                                            <Field name='weddingCity'>
                                                {({ field }: FieldProps) => (
                                                    <label className={styles.weddingAlbumField}>
                                                        <FormattedMessage id='CITY_MUNICIPALITY'>
                                                            {msg => <span className={styles.label}>{msg}</span>}
                                                        </FormattedMessage>
                                                        <Input
                                                            errors={
                                                                formikBag.errors.weddingCity
                                                                    ? (formikBag.errors.weddingCity as any)
                                                                    : undefined
                                                            }
                                                            touched={formikBag.touched.weddingCity}
                                                            {...field}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                        </>
                                    ) : (
                                        <>
                                            <Field name='title'>
                                                {({ field }: FieldProps<FormValues>) => (
                                                    <label>
                                                        <div className={styles.label}>
                                                            <FormattedMessage id='ALBUM_NAME' />
                                                        </div>
                                                        <textarea
                                                            className={styles.title}
                                                            name={field.name}
                                                            value={field.value || ''}
                                                            onBlur={field.onBlur}
                                                            onChange={field.onChange}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                            <Field name='content'>
                                                {({ field }: FieldProps<FormValues>) => (
                                                    <label>
                                                        <div className={styles.label}>
                                                            <FormattedMessage id='ALBUM_DESCRIPTION' />
                                                        </div>
                                                        <textarea
                                                            className={styles.content}
                                                            name={field.name}
                                                            value={field.value || ''}
                                                            onBlur={field.onBlur}
                                                            onChange={field.onChange}
                                                        />
                                                    </label>
                                                )}
                                            </Field>
                                        </>
                                    )}
                                </div>
                                <Field name='visibility'>
                                    {({ field }: FieldProps<FormValues>) => (
                                        <AlbumBadges field={field} {...album}>
                                            <div className={styles.label}>
                                                <FormattedMessage id='ALBUM_CATEGORY' />{' '}
                                            </div>
                                            <SelectionItems initial={selectionItemIds} onChange={setSelectionItemIds} />
                                        </AlbumBadges>
                                    )}
                                </Field>
                                <div className={styles.side}>
                                    {TitleImage}
                                    <div className={styles.controls}>
                                        <button
                                            type='submit'
                                            className={styles.submitButton}
                                            disabled={formikBag.isSubmitting}
                                        >
                                            <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                                        </button>
                                        {RemoveButton}
                                        <button
                                            onClick={() => onClick(formikBag.dirty)}
                                            className={styles.cancelButton}
                                        >
                                            <SVGIcon style={{ image: styles.image }} src={iconClose} />
                                            <FormattedMessage id='LEAVE_WITHOUT_SAVING' />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </LoadingWrapperView>
                </Form>
            )}
        />
    );
};

export default Styled(defaultStyles)(InfoEdit);
